// web/src/screens/admin/tts_logs.ts
//
// /admin/tts/logs — TTS cost summary (per voice) + the chronological TTS
// call audit log. v2-aligned rebuild of v1's /admin/tts/logs. Reads the
// tts_call table that internal/tts/build writes on every TTS API call.
//
// All API-supplied text via textContent only (T-11-09a).

import { renderAdminShell } from "./admin_shell.js";
import { getTTSCosts, getTTSCalls } from "../../api/admin.js";
import type { AdminRouteProps, AdminTTSCostSummaryDTO, AdminTTSCallListDTO } from "../../types/api.js";
import { section, table, code, adminLink, navigateAdmin, fmtMicrousd, fmtTimestamp, errMessage } from "./logs_common.js";

export function mount(root: HTMLElement, _props?: unknown): () => void {
  void (_props as AdminRouteProps | undefined);

  // Within-TTS-section breadcrumb only (top nav handles cross-section nav):
  // TTS  ›  Cost & calls.
  const main = renderAdminShell(root, [
    { label: "TTS", href: "/admin/tts" },
    { label: "Cost & calls" },
  ]);

  const subnav = document.createElement("p");
  subnav.setAttribute("style", "margin: 0 0 16px 0; font-size: 14px; display: flex; gap: 16px;");
  subnav.appendChild(adminLink("← Phrases", "/admin/tts"));
  const cur = document.createElement("strong");
  cur.textContent = "Cost & calls";
  subnav.appendChild(cur);
  main.appendChild(subnav);

  let page = 1;
  { const p = parseInt(new URLSearchParams(window.location.search).get("page") ?? "1", 10); if (Number.isFinite(p) && p > 0) page = p; }

  // ── Cost by voice ────────────────────────────────────────────────────
  const costBody = section(main, "Cost by voice");
  const costStatus = document.createElement("p");
  costStatus.textContent = "Loading…";
  costStatus.setAttribute("style", "font-size: 14px; color: #6b7280; margin: 0;");
  costBody.appendChild(costStatus);

  function renderCosts(s: AdminTTSCostSummaryDTO): void {
    costStatus.remove();
    if (s.rows.length === 0) {
      const p = document.createElement("p");
      p.textContent = "No TTS calls yet.";
      p.setAttribute("style", "font-size: 14px; color: #6b7280; margin: 0;");
      costBody.appendChild(p);
      return;
    }
    const t = table(["Voice", "Provider", "Model", "Calls", "Chars", "Total cost"]);
    for (const row of s.rows) {
      t.addRow([
        row.deleted ? `${row.label} (deleted)` : row.label,
        row.provider,
        row.model || "—",
        String(row.call_count),
        String(row.total_chars),
        code(fmtMicrousd(row.total_cost_microusd)),
      ]);
    }
    costBody.appendChild(t.el);
    const grand = document.createElement("p");
    grand.setAttribute("style", "font-size: 14px; margin: 8px 0 0 0;");
    grand.append("Grand total: ");
    grand.appendChild(code(fmtMicrousd(s.grand_total_microusd)));
    costBody.appendChild(grand);
  }

  // ── Calls ────────────────────────────────────────────────────────────
  const callsBody = section(main, "Calls");
  const callsStatus = document.createElement("p");
  callsStatus.textContent = "Loading…";
  callsStatus.setAttribute("style", "font-size: 14px; color: #6b7280; margin: 0 0 8px 0;");
  callsBody.appendChild(callsStatus);
  const callsWrap = document.createElement("div");
  callsWrap.setAttribute("style", "overflow-x: auto;");
  callsBody.appendChild(callsWrap);
  const pager = document.createElement("div");
  pager.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-top: 12px; font-size: 14px;");
  callsBody.appendChild(pager);

  function renderCalls(list: AdminTTSCallListDTO): void {
    callsStatus.textContent = `${list.total} call(s) total — newest first.`;
    const t = table(["When", "Voice", "Source", "Text", "Chars", "Cost", "Error"]);
    for (const c of list.items) {
      const textCell = document.createElement("span");
      textCell.textContent = c.text.length > 80 ? `${c.text.slice(0, 80)}…` : c.text;
      if (c.text.length > 80) textCell.setAttribute("title", c.text);
      let errCell: string | Node = "—";
      if (c.error) {
        const e = document.createElement("small");
        e.setAttribute("style", "color: #991b1b; word-break: break-all;");
        e.textContent = c.error;
        errCell = e;
      }
      t.addRow([
        fmtTimestamp(c.called_at),
        c.voice_label,
        c.source || "—",
        textCell,
        String(c.char_count),
        code(fmtMicrousd(c.cost_microusd)),
        errCell,
      ]);
    }
    callsWrap.replaceChildren(t.el);

    pager.replaceChildren();
    const href = (n: number): string => (n > 1 ? `/admin/tts/logs?page=${n}` : "/admin/tts/logs");
    if (list.page > 1) {
      const b = pgBtn("‹ Previous"); b.addEventListener("click", () => navigateAdmin(href(list.page - 1))); pager.appendChild(b);
    }
    const info = document.createElement("span");
    info.textContent = `page ${list.page} / ${list.total_pages}`;
    info.setAttribute("style", "color: #6b7280;");
    pager.appendChild(info);
    if (list.page < list.total_pages) {
      const b = pgBtn("Next ›"); b.addEventListener("click", () => navigateAdmin(href(list.page + 1))); pager.appendChild(b);
    }
  }

  void (async () => {
    try {
      const s = await getTTSCosts();
      renderCosts(s);
    } catch (err) {
      costStatus.textContent = errMessage(err, "load cost summary");
    }
    try {
      const list = await getTTSCalls(page);
      renderCalls(list);
    } catch (err) {
      callsStatus.textContent = errMessage(err, "load call log");
    }
  })();

  return () => {};
}

function pgBtn(label: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  b.setAttribute("style", "padding: 6px 12px; background: white; color: #1d4ed8; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; font-size: 13px;");
  return b;
}
